import React, { useEffect, useRef } from 'react'
import { useAppSelector } from 'app/hooks'
import { Media } from 'components'
import { AppData, OrganizationData, ThumbSize } from 'app/types'

interface ImageCarouselProps {
    app?: AppData
    organization?: OrganizationData
    media: string[]
    speed?: number // Speed in seconds for one complete cycle
    spacing?: number // Spacing between items in pixels (default: 20)
    size?: number // Size of each item in pixels (default: 200)
    className?: string
    mediaProps?: any
}

export function ImageCarousel(props: ImageCarouselProps) {
    const { 
        app, 
        organization, 
        media, 
        speed = 3, 
        spacing = 20,
        size = 200,
        className = '', 
        mediaProps = {} 
    } = props

    const containerRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const itemRefs = useRef<(HTMLDivElement | null)[]>([])
    const allMedia = useAppSelector((state) => state.app.media)
    
    // Filter out any invalid media
    const validMedia = media.filter(mediaId => mediaId && allMedia[mediaId])
    
    useEffect(() => {
        if (!containerRef.current || !trackRef.current || validMedia.length === 0) return
        
        const track = trackRef.current
        const itemWidth = size + spacing
        const pixelsPerSecond = itemWidth / speed
        
        let trackPosition = 0
        let animationId: number
        let lastTime: number | null = null
        
        const animate = (currentTime: number) => {
            if (lastTime === null) {
                lastTime = currentTime
                animationId = requestAnimationFrame(animate)
                return
            }
            
            const deltaTime = currentTime - lastTime
            lastTime = currentTime
            
            // Move the track left
            const movement = (deltaTime / 1000) * pixelsPerSecond
            trackPosition -= movement
            track.style.transform = `translateX(${trackPosition}px)`
            
            // Check each item and reposition if it's gone offscreen to the left
            itemRefs.current.forEach((item, index) => {
                if (!item) return
                
                const itemRect = item.getBoundingClientRect()
                const containerRect = containerRef.current!.getBoundingClientRect()
                
                // If item has scrolled completely past the left edge
                if (itemRect.right < containerRect.left - 500) {
                    // Move it to the right of all other items
                    const currentLeft = parseFloat(item.style.left || '0')
                    const newLeft = currentLeft + (validMedia.length * itemWidth)
                    item.style.left = `${newLeft}px`
                }
            })
            
            animationId = requestAnimationFrame(animate)
        }
        
        // Initialize item positions
        itemRefs.current.forEach((item, index) => {
            if (item) {
                item.style.position = 'absolute'
                item.style.left = `${index * itemWidth}px`
                item.style.top = '0px'
            }
        })
        
        animationId = requestAnimationFrame(animate)
        
        return () => {
            if (animationId) {
                cancelAnimationFrame(animationId)
            }
        }
    }, [validMedia.length, speed, spacing, size])
    
    if (validMedia.length === 0) {
        return null
    }
    
    // Use CSS custom properties for size and spacing
    const carouselStyle = {
        '--carousel-item-size': `${size}px`,
        '--carousel-item-spacing': `${spacing}px`,
    } as React.CSSProperties
    
    return (
        <div 
            ref={containerRef}
            className={`image-carousel ${className}`}
            style={carouselStyle}
        >
            <div 
                ref={trackRef}
                className="image-carousel-track"
                style={{ 
                    position: 'relative',
                    height: `${size}px`
                }}
            >
                {validMedia.map((mediaId, index) => (
                    <div 
                        key={`${mediaId}-${index}`} 
                        className="image-carousel-item"
                        ref={el => itemRefs.current[index] = el}
                        style={{
                            width: `${size}px`,
                            height: `${size}px`,
                            marginRight: `${spacing}px`
                        }}
                    >
                        <Media
                            app={app}
                            organization={organization}
                            mediaId={mediaId}
                            fadeIn
                            thumb
                            thumbSize={ThumbSize.Large}
                            {...mediaProps}
                        />
                    </div>
                ))}
            </div>
            {/* Left fade overlay */}
            <div className="image-carousel-fade image-carousel-fade-left" />
            {/* Right fade overlay */}
            <div className="image-carousel-fade image-carousel-fade-right" />
        </div>
    )
}